import { createContext, useCallback, useEffect, useState } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

interface PWAInstallContextValue {
  canInstall: boolean;
  showBanner: boolean;
  promptInstall: () => void;
  dismissBanner: () => void;
}

export const PWAInstallContext = createContext<PWAInstallContextValue>({
  canInstall: false,
  showBanner: false,
  promptInstall: () => {},
  dismissBanner: () => {},
});

export function PWAInstallProvider({ children }: { children: React.ReactNode }) {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showBanner, setShowBanner] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Check if already in standalone mode
  const isStandalone = typeof window !== 'undefined'
    && window.matchMedia('(display-mode: standalone)').matches;

  // Capture the beforeinstallprompt event
  useEffect(() => {
    if (isStandalone) return;

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);

      // Show banner after a short delay, only if not previously dismissed
      const dismissedFlag = sessionStorage.getItem('joviaa_install_dismissed');
      if (!dismissedFlag) {
        setTimeout(() => {
          setShowBanner(true);
        }, 4000);
      }
    };

    window.addEventListener('beforeinstallprompt', handler);

    // Check if app was installed successfully
    const installedHandler = () => {
      setShowBanner(false);
      setDeferredPrompt(null);
    };
    window.addEventListener('appinstalled', installedHandler);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installedHandler);
    };
  }, [isStandalone]);

  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;

    if (choice.outcome === 'accepted') {
      setShowBanner(false);
      setDeferredPrompt(null);
    } else {
      setShowBanner(false);
      setDismissed(true);
    }
  }, [deferredPrompt]);

  const dismissBanner = useCallback(() => {
    setShowBanner(false);
    setDismissed(true);
    sessionStorage.setItem('joviaa_install_dismissed', '1');
  }, []);

  const canInstall = !!deferredPrompt && !isStandalone;

  return (
    <PWAInstallContext.Provider value={{ canInstall, showBanner, promptInstall, dismissBanner }}>
      {children}
    </PWAInstallContext.Provider>
  );
}